import { Clock as ClockIcon } from "lucide-react";

interface Props {
  // Unix seconds from the server at the moment the payload was built.
  serverTimeTs: number;
  // IANA zone name the server reported (best effort). On Windows Python this
  // can be a bare offset like ``UTC+03:00`` which Intl rejects — we fall back
  // to the browser's local zone in that case.
  timezone: string;
}

/**
 * Shows the server-side HH:MM that arrived with the last /api/status refresh.
 * Updates implicitly every 5s when the status query refetches; no local ticker.
 */
export default function ServerClock({ serverTimeTs, timezone }: Props) {
  const when = new Date(serverTimeTs * 1000);
  return (
    <div className="flex items-center justify-between rounded-xl border border-slate-800 bg-slate-900/60 px-4 py-2 text-sm">
      <span className="flex items-center gap-2 text-slate-400">
        <ClockIcon className="h-4 w-4" />
        Server time
      </span>
      <span className="font-mono text-slate-200">
        {formatHM(when, timezone)}
        {timezone ? <span className="ml-2 text-xs text-slate-500">{timezone}</span> : null}
      </span>
    </div>
  );
}

// Format as HH:MM in the server's IANA zone when possible; otherwise fall
// back to the browser's local zone. Any Intl error (e.g. an offset-only
// "UTC+03:00" label) is caught so this never crashes the page.
function formatHM(date: Date, timezone: string): string {
  const opts: Intl.DateTimeFormatOptions = { hour: "2-digit", minute: "2-digit", hour12: false };
  if (timezone) {
    try {
      return new Intl.DateTimeFormat(undefined, { ...opts, timeZone: timezone }).format(date);
    } catch {
      /* malformed zone — fall through */
    }
  }
  return new Intl.DateTimeFormat(undefined, opts).format(date);
}
